import {defineStore} from "pinia"
import {computed, ref} from "vue"
import {useFirebaseAuth} from "vuefire"
import {User} from "firebase/auth";
import {API_GATEWAY_URL} from "@/constants"
import {
  ContentApiResponse,
} from "@/types"
import {
  AllAttemptsFailedError,
  FTSpecialAccessApiError, UnauthorisedError,
  UnknownError,
} from "@/errors"
import {
  fetchFirstSuccess,
} from "@/lib/fetcher"
import { MissingClaim } from "@/errors.ts";
import { buildUrlWithParams, getNlaUsers } from "./utils";
import { ArticleEndpoint } from "@/enums.ts";
import { logger, LogCode } from '@/logger';
import { handleError } from "@/stores/article.errors.ts";

type fetchArticleResponse = {
  url: string
}

// Used in ViewArticle.vue but can't be exported to Jest due to
// https://github.com/vuejs/core/issues/8301
type FetchArticleParams = {
  ids: string;
  endpoint: ArticleEndpoint,
  branding?: string;
  mmoId?: string;
  P1?: string;
  P2?: string;
  P3?: string;
  P4?: string;
  P5?: string;
}

const getNlaUserIds = async (user: User | null): Promise<string[] | undefined> => {
  if (user) {
    const nlaUsers = await getNlaUsers(user);
    return nlaUsers?.map(user => user.id) ?? []
  }
  return undefined
}

export const useArticleStore = defineStore('article', () => {
  const _ids = ref<string>('');
  const _endpoint = ref<ArticleEndpoint>(ArticleEndpoint.SINGLE_ARTICLE);
  const _branding = ref<string>();
  const _mmoId = ref<string>();
  const _p1 = ref<string>();
  const _p2 = ref<string>();
  const _p3 = ref<string>();
  const _p4 = ref<string>();
  const _p5 = ref<string>();
  const _user = useFirebaseAuth()!.currentUser;
  const _fetchArticleUrl = computed(() => {
    const idString = _ids.value;
    const commonParams = {
      branding: _branding.value,
      mmoId: _mmoId.value,
      P1: _p1.value,
      P2: _p2.value,
      P3: _p3.value,
      P4: _p4.value,
      P5: _p5.value
    }
    // Build URL for single article or multiple articles
    // Endpoints behave differently upon errors
    const {params, endpoint} = (() => {
      switch (_endpoint.value) {
        case ArticleEndpoint.SINGLE_ARTICLE:
          return {endpoint: `/articles/${idString}`, params: commonParams}
        case ArticleEndpoint.MULTIPLE_ARTICLES:
          return {endpoint: "articles", params: {...commonParams, ids: idString}}
        default:
          throw new Error(`Unknown endpoint option: ${_endpoint.value}`)
      }
    })();
    const url = buildUrlWithParams(endpoint, API_GATEWAY_URL, params);
    return url.toString();
  });
  const isFinished = ref<boolean>(false);
  const error = ref<string>();
  const _fetchArticle = async (correlationId: string): Promise<ContentApiResponse<fetchArticleResponse>> => {
    if (!_user) throw new UnauthorisedError("User not found");
    const tokenString = await _user.getIdToken();
    const nlaUsers = await getNlaUsers(_user);

    if (nlaUsers === undefined){
      throw new MissingClaim("nlaUsers");
    }
    try {
      const resp = await fetchFirstSuccess(nlaUsers, _fetchArticleUrl.value, tokenString, correlationId);
      return await resp.json();
    }
    catch (e) {
      console.log('_fetchArticle caught error', e)
      let error = e;
      // Extract error if only one request was made
      if(e instanceof AllAttemptsFailedError){
        error = e.getSingularError() ?? e;
      }

      if (error instanceof Error) {
        throw error;
      }
      throw new UnknownError();
    }
  }
  const fetchArticle = async (params: FetchArticleParams, correlationId: string) => {
    isFinished.value = false;
    _ids.value = params.ids;
    _endpoint.value = params.endpoint;
    _branding.value = params.branding;
    _mmoId.value = params.mmoId;
    _p1.value = params.P1;
    _p2.value = params.P2;
    _p3.value = params.P3;
    _p4.value = params.P4;
    _p5.value = params.P5;
    try {
      const json = await _fetchArticle(correlationId);
      window.open(json.data.url, '_self');
      return Promise.resolve();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    catch (e: any) {
      const {
        errorToThrow, userMessage, logMessage, logCode
      } = handleError(e, correlationId);

      error.value = userMessage;

      if (e instanceof FTSpecialAccessApiError) {
        window.open(e.url, '_self');
      }

      const nlaUserIds = await getNlaUserIds(_user);

      logger.error(
        logMessage ?? `Failed to fetch article`,
        {
          url: _endpoint.value,
          component: 'stores/article.ts',
          correlationId,
          pathname: window.location.pathname,
          route: window.location.pathname + window.location.search,
          logCode: logCode as LogCode ?? LogCode.FAILED_TO_FETCH_ARTICLE,
          dataFields: {
            ...params,
            mmoId: _mmoId.value,
            articleIds: _ids.value,
            userIds: nlaUserIds,
            branding: _branding.value,
          },
        },
        errorToThrow,
      );
    } finally {
      isFinished.value = true;
    }
  }
  return {
    isFinished,
    error,
    fetchArticle
  }
});
